/**
 * Locations API Contract — Regions, Areas, and Area Postcodes (works-lite)
 *
 * Base path: /works-lite/locations  (mounted in apps/backend/src/routes/index.ts)
 *
 * Model: Region (optional) → contains Areas → each Area contains Postcodes.
 * Teams are assigned to Areas and/or Regions; availability matching resolves a
 * job postcode → area(s) → team_area teams, plus those areas' regions →
 * team_region teams.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

const c = initContract();

const errorResponseSchema = z.object({
  error: z.object({
    message: z.string(),
    code: z.string().optional(),
  }),
});

// ---- Regions ----

export const regionSchema = z.object({
  id: z.number(),
  name: z.string().nullable(),
  area_count: z.number(),
});

export const regionsResponseSchema = z.array(regionSchema);

export const createRegionBodySchema = z.object({
  name: z.string().min(1).max(255),
});
export const updateRegionBodySchema = createRegionBodySchema.partial();

// ---- Areas ----

export const areaSchema = z.object({
  id: z.number(),
  name: z.string().nullable(),
  contractor: z.string().nullable(),
  region_id: z.number().nullable(),
  region_name: z.string().nullable(),
  // In the paginated LIST response this is a bounded PREVIEW (first few
  // postcodes) — an area can hold tens of thousands, so the list never returns
  // them all. Fetch the full set via GET /areas/:id. In the single-area and
  // create/update responses this is the COMPLETE list.
  postcodes: z.array(z.string()),
  // Total non-deleted postcodes for the area (cheap COUNT), so the list can show
  // "+N" without shipping every postcode.
  postcode_count: z.number(),
});

// Paginated list response — the area table can hold 100k+ rows, so areas are
// always served a page at a time with an optional search.
export const areasResponseSchema = z.object({
  items: z.array(areaSchema),
  total: z.number(),
  limit: z.number(),
  skip: z.number(),
});

export const areasQuerySchema = z.object({
  search: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).optional(),
  skip: z.coerce.number().int().min(0).optional(),
});

export const createAreaBodySchema = z.object({
  name: z.string().min(1).max(50),
  contractor: z.string().max(255).optional().nullable(),
  region_id: z.number().optional().nullable(),
  postcodes: z.array(z.string().min(1).max(16)).optional(),
});
export const updateAreaBodySchema = createAreaBodySchema.partial();

export type Region = z.infer<typeof regionSchema>;
export type RegionsResponse = z.infer<typeof regionsResponseSchema>;
export type CreateRegionBody = z.infer<typeof createRegionBodySchema>;
export type UpdateRegionBody = z.infer<typeof updateRegionBodySchema>;
export type Area = z.infer<typeof areaSchema>;
export type AreasResponse = z.infer<typeof areasResponseSchema>;
export type AreasQuery = z.infer<typeof areasQuerySchema>;
export type CreateAreaBody = z.infer<typeof createAreaBodySchema>;
export type UpdateAreaBody = z.infer<typeof updateAreaBodySchema>;

export const locationsContract = c.router({
  // ---- Regions ----
  getRegions: {
    method: 'GET',
    path: '/regions',
    responses: { 200: regionsResponseSchema, 500: errorResponseSchema },
    summary: 'List active regions with area counts',
  },
  createRegion: {
    method: 'POST',
    path: '/regions',
    body: createRegionBodySchema,
    responses: { 201: regionSchema, 400: errorResponseSchema, 500: errorResponseSchema },
    summary: 'Create a region',
  },
  updateRegion: {
    method: 'PUT',
    path: '/regions/:id',
    pathParams: z.object({ id: z.coerce.number() }),
    body: updateRegionBodySchema,
    responses: { 200: regionSchema, 404: errorResponseSchema, 500: errorResponseSchema },
    summary: 'Update a region',
  },
  deleteRegion: {
    method: 'DELETE',
    path: '/regions/:id',
    pathParams: z.object({ id: z.coerce.number() }),
    body: z.any().optional(),
    responses: {
      200: z.object({ success: z.boolean() }),
      404: errorResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Soft-delete a region (its areas are detached, not deleted)',
  },

  // ---- Areas ----
  getAreas: {
    method: 'GET',
    path: '/areas',
    query: areasQuerySchema,
    responses: { 200: areasResponseSchema, 500: errorResponseSchema },
    summary: 'List active areas (paginated, searchable) with region and postcodes',
  },
  getArea: {
    method: 'GET',
    path: '/areas/:id',
    pathParams: z.object({ id: z.coerce.number() }),
    responses: { 200: areaSchema, 404: errorResponseSchema, 500: errorResponseSchema },
    summary: 'Get a single area with its COMPLETE postcode list',
  },
  createArea: {
    method: 'POST',
    path: '/areas',
    body: createAreaBodySchema,
    responses: { 201: areaSchema, 400: errorResponseSchema, 500: errorResponseSchema },
    summary: 'Create an area with its postcode list',
  },
  updateArea: {
    method: 'PUT',
    path: '/areas/:id',
    pathParams: z.object({ id: z.coerce.number() }),
    body: updateAreaBodySchema,
    responses: { 200: areaSchema, 404: errorResponseSchema, 500: errorResponseSchema },
    summary: 'Update an area and replace its postcode list',
  },
  deleteArea: {
    method: 'DELETE',
    path: '/areas/:id',
    pathParams: z.object({ id: z.coerce.number() }),
    body: z.any().optional(),
    responses: {
      200: z.object({ success: z.boolean() }),
      404: errorResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Soft-delete an area',
  },
});
